import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { PaneviewComponent } from 'dockview-core';
import 'dockview-core/dist/styles/dockview.css';

/**
 * DashPaneLayout is a component that provides collapsible panes (accordion style).
 * Each pane has a header that can be clicked to expand/collapse the content.
 */
const DashPaneLayout = (props) => {
    const {
        id,
        setProps,
        children,
        panels = [],
        layout = null,
        savedLayout = null,
        theme = 'dockview-theme-light',
        height = '600px',
        disableDnd = false,
        loading_state = null,
    } = props;

    const containerRef = useRef(null);
    const paneviewApiRef = useRef(null);
    const [panelContainers, setPanelContainers] = useState({});
    const [isReady, setIsReady] = useState(false);
    const disposablesRef = useRef([]);

    // Create a mapping of panel IDs to children
    const childrenMap = useMemo(() => {
        const map = {};
        if (children) {
            const childArray = Array.isArray(children) ? children : [children];
            childArray.forEach((child) => {
                if (child && child.props) {
                    const childId = child.props.id ||
                                   child.props._dashprivate_layout?.props?.id ||
                                   child.key;
                    if (childId) {
                        map[childId] = child;
                    }
                }
            });
        }
        return map;
    }, [children]);

    // Initialize paneview
    useEffect(() => {
        if (!containerRef.current) return;

        const paneview = new PaneviewComponent(containerRef.current, {
            disableDnd,
            createComponent: (options) => {
                // Create the container element for this panel's content
                const element = document.createElement('div');
                element.style.height = '100%';
                element.style.width = '100%';
                element.style.overflow = 'auto';
                element.style.boxSizing = 'border-box';
                element.className = 'dash-pane-panel-content';
                element.setAttribute('data-panel-id', options.id);

                // Return a component with the required interface (including update method)
                return {
                    element,
                    init: (params) => {
                        // Panel is fully initialized and element is in DOM
                        // Update state to trigger portal creation
                        setPanelContainers(prev => ({
                            ...prev,
                            [options.id]: element
                        }));
                    },
                    update: (params) => {
                        // Handle parameter updates if needed
                    },
                    dispose: () => {
                        // Remove from state when panel is disposed
                        setPanelContainers(prev => {
                            const next = { ...prev };
                            delete next[options.id];
                            return next;
                        });
                    }
                };
            },
        });

        paneviewApiRef.current = paneview;

        // Setup event listeners
        const layoutDisposable = paneview.onDidLayoutChange(() => {
            if (setProps) {
                try {
                    setProps({ layout: paneview.toJSON() });
                } catch (err) {
                    console.error('Failed to serialize layout:', err);
                }
            }
        });
        disposablesRef.current.push(layoutDisposable);

        // Load initial panels
        if (panels.length > 0) {
            panels.forEach((panel, index) => {
                const panelId = panel.id || `panel-${index}`;
                paneview.addPanel({
                    id: panelId,
                    component: 'default',
                    title: panel.title || `Panel ${index + 1}`,
                    isExpanded: panel.isExpanded !== false,
                    minimumBodySize: panel.minimumSize,
                    maximumBodySize: panel.maximumSize,
                });
            });
        }

        if (setProps) {
            setProps({ panelCount: paneview.panels.length });
        }

        setIsReady(true);

        return () => {
            disposablesRef.current.forEach(d => d.dispose());
            disposablesRef.current = [];
            setPanelContainers({});
            paneview.dispose();
            paneviewApiRef.current = null;
            setIsReady(false);
        };
    }, [theme, disableDnd]);

    // Load saved layout
    useEffect(() => {
        if (paneviewApiRef.current && savedLayout) {
            try {
                paneviewApiRef.current.fromJSON(savedLayout);
            } catch (err) {
                console.error('Failed to load layout:', err);
            }
        }
    }, [savedLayout]);

    // Render portals
    const portals = useMemo(() => {
        if (!isReady) return [];

        return Object.entries(panelContainers).map(([panelId, container]) => {
            if (!container || !document.body.contains(container)) {
                return null;
            }

            const child = childrenMap[panelId];
            if (child) {
                return ReactDOM.createPortal(
                    <div style={{ height: '100%', width: '100%', overflow: 'auto' }} key={panelId}>
                        {child}
                    </div>,
                    container
                );
            }
            return ReactDOM.createPortal(
                <div style={{ padding: '1rem', color: 'var(--dv-foreground-color, #333)' }} key={panelId}>
                    <p>Panel: {panelId}</p>
                </div>,
                container
            );
        }).filter(Boolean);
    }, [panelContainers, childrenMap, isReady]);

    return (
        <div id={id} className={theme} style={{ width: '100%', height }}>
            <div
                ref={containerRef}
                className={theme}
                style={{
                    height: '100%',
                    backgroundColor: 'var(--dv-background-color)',
                    border: '1px solid var(--dv-separator-border)'
                }}
            />
            {portals}
        </div>
    );
};

DashPaneLayout.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string.isRequired,

    /**
     * Dash components to render in panels. Each child should have an id matching a panel id.
     */
    children: PropTypes.node,

    /**
     * Initial panels to display.
     */
    panels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string,
        isExpanded: PropTypes.bool,
        minimumSize: PropTypes.number,
        maximumSize: PropTypes.number,
    })),

    /**
     * Current layout configuration (read-only)
     */
    layout: PropTypes.object,

    /**
     * Layout JSON to load
     */
    savedLayout: PropTypes.object,

    /**
     * Theme class name
     */
    theme: PropTypes.string,

    /**
     * Height of the component
     */
    height: PropTypes.string,

    /**
     * Disable drag-and-drop for reordering panes
     */
    disableDnd: PropTypes.bool,

    /**
     * Total number of panels
     */
    panelCount: PropTypes.number,

    /**
     * Dash-assigned callback
     */
    setProps: PropTypes.func,

    /**
     * Dash loading state
     */
    loading_state: PropTypes.shape({
        is_loading: PropTypes.bool,
        prop_name: PropTypes.string,
        component_name: PropTypes.string,
    }),
};

export default DashPaneLayout;
